import Vue from 'vue';
import { BackEndService } from 'src/utils/BackEndService';
import type { Skill, SkillCategory } from './models/SkillModels';
import type { Exercise } from './models/Exercise';
import type { Challenge } from '../playable/models/Challenge';
import { type RequestState } from '../global/types/TechTypes';
import { videoService } from './VideoService';

interface FinishedRevisionProperties<T> {
  finished: T[];
  revision: {
    challenge?: T;
    exercise?: T
  }[];
}
interface SkillServiceState {
  categories: SkillCategory[];
  categoriesRequestState: RequestState;
  skillsByCategory: Record<number, Skill[]>;
  skillsByCategoryRequestState: Record<number, RequestState>;
  skillDetails: Record<number, Skill>;
  skillDetailsRequestState: Record<number, RequestState>;
  skillExercises: Record<number, FinishedRevisionProperties<Exercise>>;
  skillExercisesRequestState: Record<number, RequestState>;
  skillChallenges: Record<number, FinishedRevisionProperties<Challenge>>;
  skillChallengesRequestState: Record<number, RequestState>;
}

class SkillService {
  public readonly state = Vue.observable<SkillServiceState>({
    categories: [],
    categoriesRequestState: 'idle',
    skillsByCategory: {},
    skillsByCategoryRequestState: {},
    skillDetails: {},
    skillDetailsRequestState: {},
    skillExercises: {},
    skillExercisesRequestState: {},
    skillChallenges: {},
    skillChallengesRequestState: {},
  });

  public isSkillCategoriesIsFinished(currentDifficulty: number): boolean {
    return this.state.categories.filter((category) => category.difficulty === currentDifficulty).every(category => category.status === 'Validated');
  }

  private categoriesPromise: Promise<SkillCategory[]> | null = null;
  async loadCategories(force = false): Promise<SkillCategory[]> {
    if (this.state.categoriesRequestState === 'loading' && this.categoriesPromise) {
      return this.categoriesPromise
    }

    if (this.state.categoriesRequestState !== 'loaded' || force) {
      this.state.categoriesRequestState = 'loading';

      this.categoriesPromise = BackEndService.sendGetRequest(`skill-model/skill-categories`)
        .then(response => {
          this.state.categories = response;
          this.state.categoriesRequestState = 'loaded';
          return response;
        })
        .catch(error => {
          console.error('Error loading skill categories:', error);
          this.state.categoriesRequestState = 'error';
          return [];
        });
    }

    if (this.categoriesPromise) {
      return this.categoriesPromise
    }

    return Promise.resolve(this.state.categories);
  }

  private skillsForCategoryPromise: Record<number, Promise<Skill[]> | undefined> = {};
  refreshSkillForCategory(categoryId: number): void {
    this.state.skillsByCategoryRequestState = { ...this.state.skillsByCategoryRequestState, [categoryId]: 'idle' }
  }
  async loadSkillsForCategory(categoryId: number, force = false): Promise<Skill[]> {
    if (this.state.skillsByCategoryRequestState[categoryId] === 'loading' && this.skillsForCategoryPromise[categoryId]) {
      return this.skillsForCategoryPromise[categoryId]
    }

    if (this.state.skillsByCategoryRequestState[categoryId] !== 'loaded' || force) {
      this.state.skillsByCategoryRequestState = { ...this.state.skillsByCategoryRequestState, [categoryId]: 'loading' };
      this.skillsForCategoryPromise[categoryId] = BackEndService.sendGetRequest(`skill-model/skill-categories/${categoryId}/skills`)
        .then(response => {
          this.state.skillsByCategory = { ...this.state.skillsByCategory, [categoryId]: response };
          this.state.skillsByCategoryRequestState = { ...this.state.skillsByCategoryRequestState, [categoryId]: 'loaded' };
          return response;
        })
        .catch(error => {
          console.error(`Error loading skills for category ${categoryId}:`, error);
          this.state.skillsByCategoryRequestState = { ...this.state.skillsByCategoryRequestState, [categoryId]: 'error' };
          return [];
        });

    }

    if (this.skillsForCategoryPromise[categoryId]) {
      return this.skillsForCategoryPromise[categoryId]
    }

    return Promise.resolve(this.state.skillsByCategory[categoryId] || []);
  }

  private skillDetailsPromise: Record<number, Promise<Skill> | undefined> = {};
  refreshSkillDetails(skillId: number): void {
    this.state.skillDetailsRequestState = { ...this.state.skillDetailsRequestState, [skillId]: 'idle' }
  }
  async loadSkillDetail(skillId: number, force = false): Promise<Skill> {
    if (this.state.skillDetailsRequestState[skillId] === 'loading' && this.skillDetailsPromise[skillId]) {
      return this.skillDetailsPromise[skillId]
    }

    if (this.state.skillDetailsRequestState[skillId] !== 'loaded' || force) {
      this.state.skillDetailsRequestState = { ...this.state.skillDetailsRequestState, [skillId]: 'loading' };
      this.skillDetailsPromise[skillId] = BackEndService.sendGetRequest(`skill-model/skills/${skillId}`)
        .then(response => {
          this.state.skillDetails = { ...this.state.skillDetails, [skillId]: response };
          console.warn(this.state.skillDetails)
          this.state.skillDetailsRequestState = { ...this.state.skillDetailsRequestState, [skillId]: 'loaded' };
          return response;
        })
        .catch(error => {
          console.error(`Error loading skill details for skill ${skillId}:`, error);
          this.state.skillDetailsRequestState = { ...this.state.skillDetailsRequestState, [skillId]: 'error' };
          return null;
        });
    }

    if (this.skillDetailsPromise[skillId]) {
      return this.skillDetailsPromise[skillId]
    }

    return Promise.resolve(this.state.skillDetails[skillId] || null);
  }

  private skillExercisesPromise: Record<number, Promise<FinishedRevisionProperties<Exercise>> | undefined> = {};
  refreshSkillExercises(skillId: number): void {
    this.state.skillExercisesRequestState = { ...this.state.skillExercisesRequestState, [skillId]: 'idle' }
  }
  async loadSkillExercises(skillId: number, force = false): Promise<FinishedRevisionProperties<Exercise>> {
    if (this.state.skillExercisesRequestState[skillId] === 'loading' && this.skillExercisesPromise[skillId]) {
      return this.skillExercisesPromise[skillId]
    }

    if (this.state.skillExercisesRequestState[skillId] !== 'loaded' || force) {
      this.state.skillExercisesRequestState = { ...this.state.skillExercisesRequestState, [skillId]: 'loading' };
      this.skillExercisesPromise[skillId] = BackEndService.sendGetRequest(`skill-model/skills/${skillId}/exercises`)
        .then(response => {
          this.state.skillExercises = { ...this.state.skillExercises, [skillId]: response };
          this.state.skillExercisesRequestState = { ...this.state.skillExercisesRequestState, [skillId]: 'loaded' };
          return response;
        })
        .catch(error => {
          console.error(`Error loading exercises for skill ${skillId}:`, error);
          this.state.skillExercisesRequestState = { ...this.state.skillExercisesRequestState, [skillId]: 'error' };
          return [];
        });
    }

    if (this.skillExercisesPromise[skillId]) {
      return this.skillExercisesPromise[skillId]
    }

    return Promise.resolve(this.state.skillExercises[skillId] || { finished: [], revision: [] });
  }

  private skillChallengesPromise: Record<number, Promise<FinishedRevisionProperties<Challenge>> | undefined> = {};
  refreshSkillChallenges(skillId: number): void {
    this.state.skillChallengesRequestState = { ...this.state.skillChallengesRequestState, [skillId]: 'idle' }
  }
  async loadSkillChallenges(skillId: number, force = false): Promise<FinishedRevisionProperties<Challenge>> {
    if (this.state.skillChallengesRequestState[skillId] === 'loading' && this.skillChallengesPromise[skillId]) {
      return this.skillChallengesPromise[skillId]
    }

    if (this.state.skillChallengesRequestState[skillId] !== 'loaded' || force) {
      this.state.skillChallengesRequestState = { ...this.state.skillChallengesRequestState, [skillId]: 'loading' };
      this.skillChallengesPromise[skillId] = BackEndService.sendGetRequest(`skill-model/skills/${skillId}/challenges`)
        .then(response => {
          this.state.skillChallenges = { ...this.state.skillChallenges, [skillId]: response };
          this.state.skillChallengesRequestState = { ...this.state.skillChallengesRequestState, [skillId]: 'loaded' };
          return response;
        })
        .catch(error => {
          console.error(`Error loading challenges for skill ${skillId}:`, error);
          this.state.skillChallengesRequestState = { ...this.state.skillChallengesRequestState, [skillId]: 'error' };
          return [];
        });
    }

    if (this.skillChallengesPromise[skillId]) {
      return this.skillChallengesPromise[skillId]
    }

    return Promise.resolve(this.state.skillChallenges[skillId] || { finished: [], revision: [] });
  }

  resetState() {
    this.state.categoriesRequestState = 'idle';
    this.state.skillsByCategoryRequestState = {};
    this.state.skillDetailsRequestState = {};
    this.state.skillExercisesRequestState = {};
    this.state.skillChallengesRequestState = {};
    this.categoriesPromise = null;
    this.skillsForCategoryPromise = {};
    this.skillDetailsPromise = {};
    this.skillExercisesPromise = {};
    this.skillChallengesPromise = {};

    videoService.resetState();
  }

  async loadSkillProgressData(level: number){
    const categories = await this.loadCategories();

    const categoriesWithProgress = categories.filter(category => category.difficulty === level).map(category => ({
      ...category,
      isValidated: category.status === 'Validated',
      progressValue: category.validation_value > 0 ? category.completion / category.validation_value : 0
    }));

    const validatedCount = categoriesWithProgress.filter(cat => cat.isValidated).length;
    const totalCount = categoriesWithProgress.length;
    return {
      categories: categoriesWithProgress,
      validatedCount,
      totalCount,
      progressValue: totalCount > 0 ? validatedCount / totalCount : 0,
      progressText: `${validatedCount} / ${totalCount}`
    };
  }
}

export const skillService = new SkillService();
